import { Injectable, signal } from '@angular/core';
import { IDefinition } from '../interfaces/IDefinitions';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { IGenericServiceResponse } from '../interfaces/IServiceResponse';
import { environment } from '../../environments/environment';
import { IAppointment } from '../interfaces/Appointment/IAppointment';
import { IRolePersonel } from '../interfaces/Appointment/IAppointmentRoles';
import {
  IAppointmentDropdownData,
  IAppointmentStatus,
} from '../interfaces/Appointment/IAppointmentDropdownData';
import { PermissionService } from './permission.service';
import { IAppointmentOverlapsData } from '../interfaces/Appointment/IAppointmentOverlaps';

@Injectable({ providedIn: 'root' })
export class AppointmentService {
  public userIDsignal = signal('');
  public definitionsSignal = signal([] as IDefinition[]);

  // New BehaviorSubjects for status lists
  private appointmentStatusSubject = new BehaviorSubject<IAppointmentStatus[]>(
    []
  );
  private slotStatusSubject = new BehaviorSubject<IAppointmentStatus[]>([]);
  private participantStatusSubject = new BehaviorSubject<IAppointmentStatus[]>(
    []
  );

  // Expose observables for components to subscribe
  appointmentStatus$ = this.appointmentStatusSubject.asObservable();
  slotStatus$ = this.slotStatusSubject.asObservable();
  participantStatus$ = this.participantStatusSubject.asObservable();

  constructor(
    private _http: HttpClient,
    private readonly permissionService: PermissionService
  ) {}

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  getAppointments(
    scope: 'mine' | 'team' = 'mine'
  ): Observable<IGenericServiceResponse<IAppointment[]>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    const url = // const url = this.hasPermission(['a-app']) // UNCOMMENT THIS ONCE THE MEDICAL TEAMS ARE DEVELOPED!!!!
      scope === 'team'
        ? `${environment.BACKEND_URL}Appointment/All`
        : `${environment.BACKEND_URL}Appointment/User`;

    return this._http.get<IGenericServiceResponse<IAppointment[]>>(url, {
      headers,
    });
  }

  getAppointmentsById(
    id: string
  ): Observable<IGenericServiceResponse<IAppointment>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IAppointment>>(
      `${environment.BACKEND_URL}Appointment/${id}`,
      {
        headers: headers,
      }
    );
  }

  checkForConflicts(
    requestBody: IAppointment,
    appointmentId?: string
  ): Observable<IGenericServiceResponse<IAppointmentOverlapsData>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (appointmentId) {
      return this._http.post<IGenericServiceResponse<IAppointmentOverlapsData>>(
        `${environment.BACKEND_URL}Appointment/CheckOverlaps?currentAppointmentId=${appointmentId}`,
        requestBody,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.post<IGenericServiceResponse<IAppointmentOverlapsData>>(
        `${environment.BACKEND_URL}Appointment/CheckOverlaps`,
        requestBody,
        {
          headers: headers,
        }
      );
    }
  }
  newAppointment(
    requestBody: IAppointment
  ): Observable<IGenericServiceResponse<number>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IGenericServiceResponse<number>>(
      `${environment.BACKEND_URL}Appointment`,
      requestBody,
      {
        headers: headers,
      }
    );
  }

  updateAppointment(
    id: string,
    requestBody: IAppointment,
    updateType?: number
  ): Observable<IGenericServiceResponse<IAppointment>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const url = updateType
      ? `${environment.BACKEND_URL}Appointment/${id}?stateId=${updateType}`
      : `${environment.BACKEND_URL}Appointment/${id}`;
    return this._http.put<IGenericServiceResponse<IAppointment>>(
      url,
      requestBody,
      {
        headers: headers,
      }
    );
  }

  deleteAppointment(
    appointmentId: number
  ): Observable<IGenericServiceResponse<any>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IGenericServiceResponse<any>>(
      `${environment.BACKEND_URL}Appointment/${appointmentId}`,
      {
        headers: headers,
      }
    );
  }

  deleteAppointmentSlots(
    appointmentSlotId: number,
    stateId: number
  ): Observable<IGenericServiceResponse<any>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IGenericServiceResponse<any>>(
      `${environment.BACKEND_URL}Appointment/Slots/${appointmentSlotId}/${stateId}`,
      {
        headers: headers,
      }
    );
  }

  getUsersByRoleDropdownData(): Observable<
    IGenericServiceResponse<IRolePersonel[]>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IRolePersonel[]>>(
      `${environment.BACKEND_URL}Appointment/UsersByRoleDropdown`,
      {
        headers: headers,
      }
    );
  }

  getDropdownData(): Observable<
    IGenericServiceResponse<IAppointmentDropdownData>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IAppointmentDropdownData>>(
      `${environment.BACKEND_URL}Appointment/DropdownData`,
      {
        headers: headers,
      }
    );
  }

  // Fetch dropdown data and store it in BehaviorSubjects
  loadAppointmentStatuses(): void {
    this.getDropdownData().subscribe(
      (response) => {
        if (response?.succeed && response.data) {
          const statuses = response.data.statuses || [];

          // Update BehaviorSubjects
          this.appointmentStatusSubject.next(
            statuses.filter((s) => s.typeId === 1)
          );
          this.slotStatusSubject.next(statuses.filter((s) => s.typeId === 2));
          this.participantStatusSubject.next(
            statuses.filter((s) => s.typeId === 3)
          );
        }
      },
      (error) => {
        console.error('Error loading dropdown data:', error);
      }
    );
  }
}
